import type { BBJEvent } from "@api";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ComponentProps, type ComponentType, createElement, useEffect, useRef, useState } from "react";

export const useStartReachedObserver = (
  element: HTMLElement | null,
  onStartReached?: () => void,
  startReachedOffset = 0
) => {
  useEffect(() => {
    if (element && onStartReached) {
      const onScroll = () => {
        if (element.scrollTop - startReachedOffset <= 0) {
          onStartReached();
        }
      };
      element.addEventListener("scroll", onScroll);
      return () => element.removeEventListener("scroll", onScroll);
    }
  }, [element, onStartReached, startReachedOffset]);
};

export const useEndReachedObserver = (
  element: HTMLElement | null,
  onEndReached?: (scrollTop: number) => void,
  endReachedOffset = 50
) => {
  useEffect(() => {
    if (element && onEndReached) {
      const onScroll = () => {
        if (element.scrollHeight - element.scrollTop - endReachedOffset <= element.clientHeight) {
          onEndReached(element.scrollTop);
        }
      };
      element.addEventListener("scroll", onScroll);

      return () => element.removeEventListener("scroll", onScroll);
    }
  }, [element, onEndReached, endReachedOffset]);
};

export const useScrollUpObserver = (element: HTMLElement | null, onScrollUp?: () => void) => {
  const lastScrollTop = useRef(0);

  useEffect(() => {
    if (element && onScrollUp) {
      const onScroll = () => {
        if (element.scrollTop < lastScrollTop.current) {
          onScrollUp();
        }
        lastScrollTop.current = element.scrollTop;
      };
      element.addEventListener("scroll", onScroll);
      return () => element.removeEventListener("scroll", onScroll);
    }
  }, [element, onScrollUp]);
};

export const useScrollDownObserver = (element: HTMLElement | null, onScrollDown?: () => void) => {
  const lastScrollTop = useRef(0);

  useEffect(() => {
    if (element && onScrollDown) {
      const onScroll = () => {
        if (element.scrollTop > lastScrollTop.current) {
          onScrollDown();
        }
        lastScrollTop.current = element.scrollTop;
      };
      element.addEventListener("scroll", onScroll);
      return () => element.removeEventListener("scroll", onScroll);
    }
  }, [element, onScrollDown]);
};

export const useScrollObserver = (
  callbacks: {
    onStartReached?: () => void;
    onEndReached?: (scrollTop: number) => void;
    onScrollUp?: () => void;
    onScrollDown?: () => void;
  },
  options?: { startReachedOffset?: number; endReachedOffset?: number }
) => {
  const [element, setElement] = useState<HTMLElement | null>(null);

  useEndReachedObserver(element, callbacks.onEndReached, options?.endReachedOffset);
  useStartReachedObserver(element, callbacks.onStartReached, options?.startReachedOffset);
  useScrollUpObserver(element, callbacks.onScrollUp);
  useScrollDownObserver(element, callbacks.onScrollDown);

  return { element, setElement };
};

type TScrollObserverProps<C extends ComponentType<any>> = {
  component: C;
  onStartReached?: () => void;
  startReachedOffset?: number;
  onEndReached?: (scrollTop: number) => void;
  endReachedOffset?: number;
  onScrollUp?: () => void;
  onScrollDown?: () => void;
  onScrollToBottom?: BBJEvent<[], () => void> | null;
} & Omit<ComponentProps<C>, "component" | "onEndReached" | "endReachedOffset">;

export const ScrollObserver = <C extends ComponentType<any>>({
  component,
  onStartReached,
  startReachedOffset,
  onEndReached,
  endReachedOffset,
  onScrollUp,
  onScrollDown,
  onScrollToBottom,
  ...props
}: TScrollObserverProps<C>) => {
  const { element, setElement } = useScrollObserver(
    { onStartReached, onEndReached, onScrollUp, onScrollDown },
    { startReachedOffset, endReachedOffset }
  );

  useEffect(() => {
    if (onScrollToBottom) {
      const scrollToBottom = () => {
        element?.scrollTo(0, element.scrollHeight);
        if (onEndReached && element) {
          onEndReached(element.scrollTop);
        }
      };
      onScrollToBottom.register(scrollToBottom);
      return () => onScrollToBottom?.unregister(scrollToBottom);
    }
  }, [onScrollToBottom, element, onEndReached]);

  return createElement(component, { ...props, ref: setElement });
};
